/**
 * Onboarding Step 3 — Connect ad platforms.
 *
 * 2026-05-23 update: skip checkbox is now LOCAL INTENT, not an immediate
 * /skip API call. This page owns the willSkipGoogle + willSkipMeta state,
 * passes to each card via props. Continue button commits any pending skips
 * via POST /onboarding/skip, then navigates.
 *
 * Cards no longer collapse on skip-check during onboarding. User can
 * toggle freely. State transitions are clean (check, uncheck, then connect
 * all work without weird intermediate states).
 *
 * On mount, local skip state is auto-initialized from DB skip columns
 * (covers users who return to this page after a prior commit).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { computeRailState } from '@/lib/onboardingState';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { GoogleAdsCard } from '@/components/connect/GoogleAdsCard';
import { FacebookAdsCard } from '@/components/connect/FacebookAdsCard';

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Connection canceled. Click Connect to try again.',
  missing_params: 'Something went wrong with the OAuth redirect. Please try again.',
  invalid_state: 'Your connection link expired. Please click Connect again.',
  token_exchange: "Couldn't exchange your authorization. Please try again.",
  no_refresh_token: "Didn't get a refresh token. Please reconnect.",
  no_token: "Didn't get an access token. Please reconnect.",
  no_client: 'Please finish Step 1 (business profile) before connecting accounts.',
  secret_create: 'Internal setup error. Please try again or contact support.',
  secret_write: 'Internal setup error. Please try again or contact support.',
  no_ads_user:
    "The Google account you signed in with doesn't have any Google Ads accounts. Sign out of that Google account and connect again with the one that owns your Ads.",
  missing_scopes:
    "We need both permissions to push contacts to Google Ads. Please reconnect and check both permission boxes on Google's screen.",
  picker_load_failed:
    "We couldn't load your Meta datasets after authorizing. Please try Connect Meta Ads again.",
};

function formatOAuthError(code: string | null, provider: 'Google' | 'Meta'): string | null {
  if (!code) return null;
  return OAUTH_ERROR_MESSAGES[code] ?? `${provider} connection failed (${code}). Please try again.`;
}

export function OnboardingConnectPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Strip OAuth error query params once on mount.
  const [initialErrors] = useState(() => ({
    google: searchParams.get('google_error'),
    facebook: searchParams.get('facebook_error'),
  }));

  useEffect(() => {
    if (initialErrors.google || initialErrors.facebook) {
      window.history.replaceState({}, '', '/onboarding/connect');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleConnected = !!me?.google_ads_customer_id;
  const googleSkippedDb = !!me?.google_ads_skipped_at;
  const facebookConnected = !!me?.facebook_pixel_id;
  const facebookSkippedDb = !!me?.facebook_skipped_at;

  // Local intent — initialized from DB skip state (covers users returning
  // to this page after a prior commit).
  const [willSkipGoogle, setWillSkipGoogle] = useState(googleSkippedDb);
  const [willSkipMeta, setWillSkipMeta] = useState(facebookSkippedDb);
  const [submitting, setSubmitting] = useState(false);

  // If me refetches and the DB skip changes (e.g., post-OAuth connect clears
  // it), sync local intent back to false so the card renders correctly.
  useEffect(() => {
    if (googleConnected) setWillSkipGoogle(false);
  }, [googleConnected]);
  useEffect(() => {
    if (facebookConnected) setWillSkipMeta(false);
  }, [facebookConnected]);

  const googleAddressed = googleConnected || willSkipGoogle;
  const facebookAddressed = facebookConnected || willSkipMeta;
  const canContinue = googleAddressed && facebookAddressed;

  async function handleContinue() {
    if (!canContinue || submitting) return;
    setSubmitting(true);
    try {
      const promises: Promise<unknown>[] = [];
      // Commit pending skips. Skip the call if DB already has the skip set.
      if (willSkipGoogle && !googleConnected && !googleSkippedDb) {
        promises.push(
          apiRequest('/onboarding/skip', {
            method: 'POST',
            body: { step: 'google_ads' },
          }),
        );
      }
      if (willSkipMeta && !facebookConnected && !facebookSkippedDb) {
        promises.push(
          apiRequest('/onboarding/skip', {
            method: 'POST',
            body: { step: 'facebook' },
          }),
        );
      }
      if (promises.length > 0) {
        await Promise.all(promises);
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      }
      navigate('/');
    } finally {
      setSubmitting(false);
    }
  }

  const googleErrorMessage = formatOAuthError(initialErrors.google, 'Google');
  const facebookErrorMessage = formatOAuthError(initialErrors.facebook, 'Meta');

  return (
    <OnboardingShell
      current={3}
      title="Connect your ad platforms"
      subtitle="Push your WhatsApp contacts back as conversions. Your ads learn from real customers."
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-5">
        {googleErrorMessage && (
          <Banner variant="error">
            <span>{googleErrorMessage}</span>
          </Banner>
        )}
        {facebookErrorMessage && (
          <Banner variant="error">
            <span>{facebookErrorMessage}</span>
          </Banner>
        )}

        <GoogleAdsCard
          showOAuthHeadsUp
          willSkip={willSkipGoogle}
          onWillSkipChange={setWillSkipGoogle}
        />
        <FacebookAdsCard
          willSkip={willSkipMeta}
          onWillSkipChange={setWillSkipMeta}
        />

        {/* Back + Continue */}
        <div className="pt-3 flex gap-3">
          <a
            href="/onboarding/website"
            className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-dark/10 px-5 py-3.5 text-base font-bold text-muted-foreground hover:bg-bg-tint hover:text-dark transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </a>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue || submitting}
            className={
              canContinue
                ? 'flex-1 rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold'
                : 'flex-1 rounded-full bg-brand/40 text-white py-3.5 text-base font-bold cursor-not-allowed'
            }
          >
            {submitting ? 'saving…' : 'Continue'}
          </Button>
        </div>
      </div>
    </OnboardingShell>
  );
}
